import { useCallback, useEffect, useState } from 'react';
import { batchesAPI, notificationsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { hasPermission } from '../utils/permissions';

const defaultCompose = {
  subject: '',
  body: '',
  channels: ['email', 'in_app'],
  audienceType: 'batch',
  role: 'student',
  batchIds: [],
  studentIdsText: '',
};

export default function useNotificationsAdmin(user) {
  const canSend = hasPermission(user, 'notifications.send');
  const canManage = hasPermission(user, 'notifications.manage');

  const [settings, setSettings] = useState(null);
  const [compose, setCompose] = useState(defaultCompose);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!canSend) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [settingsRes, campaignsRes, batchesRes] = await Promise.all([
        notificationsAPI.getSettings(),
        notificationsAPI.listCampaigns({ limit: 30 }),
        batchesAPI.getAll(),
      ]);
      setSettings(settingsRes.data?.data || null);
      setCampaigns(campaignsRes.data?.data?.items || []);
      setBatches(normalizeApiListResponse(batchesRes?.data));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  }, [canSend]);

  useEffect(() => {
    load();
  }, [load]);

  const updateCompose = (patch) => {
    setCompose((prev) => ({ ...prev, ...patch }));
    setPreview(null);
  };

  const buildPayload = () => {
    const payload = {
      subject: compose.subject.trim(),
      body: compose.body.trim(),
      channels: compose.channels,
      audienceType: compose.audienceType,
    };
    if (compose.audienceType === 'role') payload.role = compose.role;
    if (compose.audienceType === 'batch') {
      payload.batchIds = compose.batchIds.map((id) => Number(id));
    }
    if (compose.audienceType === 'students') {
      payload.studentIds = compose.studentIdsText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (compose.audienceType === 'single') {
      payload.studentId = compose.studentIdsText.trim();
    }
    return payload;
  };

  const runPreview = async () => {
    setError('');
    setSuccess('');
    try {
      const res = await notificationsAPI.preview(buildPayload());
      setPreview(res.data?.data || null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Preview failed'));
    }
  };

  const send = async () => {
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const res = await notificationsAPI.send(buildPayload());
      const summary = res.data?.data?.summary;
      setSuccess(
        `Queued notification for ${summary?.targets || 0} recipients across ${
          summary?.channels?.join(', ') || 'channels'
        }.`
      );
      setCompose(defaultCompose);
      setPreview(null);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  const saveSettings = async (patch) => {
    if (!canManage) return;
    setSavingSettings(true);
    setError('');
    setSuccess('');
    try {
      const res = await notificationsAPI.updateSettings(patch);
      setSettings(res.data?.data || null);
      setSuccess('Settings saved');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const openCampaign = async (id) => {
    setError('');
    try {
      const res = await notificationsAPI.getCampaign(id);
      setSelectedCampaign(res.data?.data || null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load campaign'));
    }
  };

  return {
    canSend,
    canManage,
    settings,
    compose,
    updateCompose,
    preview,
    runPreview,
    send,
    sending,
    campaigns,
    selectedCampaign,
    setSelectedCampaign,
    openCampaign,
    batches,
    loading,
    savingSettings,
    saveSettings,
    error,
    success,
    reload: load,
  };
}
